import React from 'react';
import { Calendar, Clock, ArrowRight } from 'lucide-react';

const HEADING_FONT = 'Manrope, sans-serif';
const BODY_FONT = 'Inter, sans-serif';

const MOCK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];
const MOCK_TIMES = ['9:00 AM', '10:00 AM', '11:00 AM', '1:00 PM', '2:00 PM', '3:00 PM'];

/**
 * Booking Block Renderer
 * Variants: inline, button, card
 */
export default function BookingRenderer({ props, variant }) {
  const {
    headline = 'Book a Meeting',
    subheadline = '',
    booking_link_id = '',
    booking_url = '#',
    button_text = 'Book Now',
    description = '',
    duration = '30 min',
    background_color = '#1c1917',
    text_color = '#ffffff',
  } = props;

  const resolvedUrl = booking_link_id ? `/booking/${booking_link_id}` : (booking_url || '#');

  // Inline variant (mock calendar)
  if (variant === 'inline') {
    return (
      <section className="py-16 px-6" style={{ backgroundColor: '#FAFAF5' }}>
        <div className="max-w-3xl mx-auto">
          {headline && (
            <div className="text-center mb-8">
              <h2
                className="text-3xl sm:text-4xl font-bold text-stone-900 mb-3"
                style={{ fontFamily: HEADING_FONT }}
              >
                {headline}
              </h2>
              {subheadline && (
                <p className="text-lg text-stone-500 max-w-2xl mx-auto" style={{ fontFamily: BODY_FONT }}>
                  {subheadline}
                </p>
              )}
            </div>
          )}
          <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Left: info */}
              <div className="p-6 border-b md:border-b-0 md:border-r border-stone-200">
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-5 h-5 text-stone-400" />
                  <span
                    className="text-sm font-semibold text-stone-900"
                    style={{ fontFamily: HEADING_FONT }}
                  >
                    Select a Date
                  </span>
                </div>
                {/* Mock calendar grid */}
                <div className="grid grid-cols-5 gap-1 text-center">
                  {MOCK_DAYS.map((d) => (
                    <div key={d} className="text-[10px] font-medium text-stone-400 py-1">
                      {d}
                    </div>
                  ))}
                  {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
                    <button
                      key={n}
                      type="button"
                      className={`text-xs py-1.5 rounded ${
                        n === 8
                          ? 'bg-stone-900 text-white font-semibold'
                          : 'text-stone-600 hover:bg-stone-50'
                      }`}
                    >
                      {n}
                    </button>
                  ))}
                </div>
                {duration && (
                  <div className="flex items-center gap-1.5 mt-4 text-xs text-stone-400">
                    <Clock className="w-3.5 h-3.5" />
                    <span style={{ fontFamily: BODY_FONT }}>{duration}</span>
                  </div>
                )}
              </div>
              {/* Right: time slots */}
              <div className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Clock className="w-5 h-5 text-stone-400" />
                  <span
                    className="text-sm font-semibold text-stone-900"
                    style={{ fontFamily: HEADING_FONT }}
                  >
                    Available Times
                  </span>
                </div>
                <div className="space-y-2">
                  {MOCK_TIMES.map((t, i) => (
                    <button
                      key={t}
                      type="button"
                      className={`w-full text-left px-4 py-2.5 rounded-lg border text-sm transition-colors ${
                        i === 2
                          ? 'border-stone-900 bg-stone-900 text-white font-medium'
                          : 'border-stone-200 text-stone-600 hover:border-stone-300 hover:bg-stone-50'
                      }`}
                      style={{ fontFamily: BODY_FONT }}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Button variant
  if (variant === 'button') {
    return (
      <section className="py-12 px-6" style={{ backgroundColor: '#FAFAF5' }}>
        <div className="max-w-xl mx-auto text-center">
          {headline && (
            <h2
              className="text-2xl sm:text-3xl font-bold text-stone-900 mb-3"
              style={{ fontFamily: HEADING_FONT }}
            >
              {headline}
            </h2>
          )}
          {subheadline && (
            <p className="text-stone-500 mb-6" style={{ fontFamily: BODY_FONT }}>
              {subheadline}
            </p>
          )}
          <a
            href={resolvedUrl}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg font-semibold text-sm sm:text-base transition-opacity hover:opacity-90"
            style={{
              backgroundColor: background_color,
              color: text_color,
              fontFamily: BODY_FONT,
            }}
          >
            <Calendar className="w-5 h-5" />
            {button_text}
            <ArrowRight className="w-4 h-4" />
          </a>
          {duration && (
            <p className="mt-3 text-xs text-stone-400 flex items-center justify-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {duration}
            </p>
          )}
        </div>
      </section>
    );
  }

  // Card variant (default)
  return (
    <section className="py-16 px-6" style={{ backgroundColor: '#FAFAF5' }}>
      <div className="max-w-lg mx-auto">
        {headline && (
          <div className="text-center mb-8">
            <h2
              className="text-3xl sm:text-4xl font-bold text-stone-900 mb-3"
              style={{ fontFamily: HEADING_FONT }}
            >
              {headline}
            </h2>
            {subheadline && (
              <p className="text-lg text-stone-500 max-w-2xl mx-auto" style={{ fontFamily: BODY_FONT }}>
                {subheadline}
              </p>
            )}
          </div>
        )}
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-8">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center flex-shrink-0">
              <Calendar className="w-6 h-6 text-stone-600" />
            </div>
            <div>
              <h3
                className="text-lg font-semibold text-stone-900 mb-1"
                style={{ fontFamily: HEADING_FONT }}
              >
                {button_text || 'Schedule a Call'}
              </h3>
              {description && (
                <p className="text-sm text-stone-500 leading-relaxed" style={{ fontFamily: BODY_FONT }}>
                  {description}
                </p>
              )}
            </div>
          </div>
          {duration && (
            <div className="flex items-center gap-1.5 mb-6 text-sm text-stone-500">
              <Clock className="w-4 h-4" />
              <span style={{ fontFamily: BODY_FONT }}>{duration}</span>
            </div>
          )}
          <a
            href={resolvedUrl}
            className="block w-full text-center px-6 py-3 rounded-lg font-semibold text-sm transition-opacity hover:opacity-90"
            style={{
              backgroundColor: background_color,
              color: text_color,
              fontFamily: BODY_FONT,
            }}
          >
            {button_text}
          </a>
        </div>
      </div>
    </section>
  );
}
